<div class="flex h-full min-h-0 w-full flex-col gap-4">
	<div class="flex flex-wrap items-center justify-between gap-3">
		<div>
			<h1 class="text-3xl font-bold">
				<span [translate]>Email Management</span>
			</h1>
			<p class="text-base-content/60 mt-2 text-sm" [translate]>
				Inspect the last 100 emails, review delivery attempts, and manually resend a message when needed.
			</p>
		</div>
		<button class="btn btn-sm btn-outline" [disabled]="isLoading() || isRefreshing()" (click)="refresh()">
			@if (isRefreshing()) {
				<span class="loading loading-spinner loading-xs"></span>
			}
			<span [translate]>Refresh</span>
		</button>
	</div>

	@if (isLoading()) {
		<div class="flex items-center gap-3">
			<span class="loading loading-spinner loading-sm"></span>
			<span class="text-base-content/60 text-sm" [translate]>Loading emails…</span>
		</div>
	} @else if (loadError()) {
		<div role="alert" class="alert alert-error">
			<span>{{ loadError() }}</span>
		</div>
	} @else {
		<div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			@for (mailSettingsRow of mailSettings(); track mailSettingsRow.id) {
				<div class="bg-base-100 border-base-200 rounded-xl border p-4 shadow-sm">
					<div class="flex items-start justify-between gap-3">
						<div>
							<div class="font-medium">{{ mailSettingsRow.name || mailSettingsRow.id }}</div>
							<div class="font-mono text-xs opacity-60">{{ mailSettingsRow.id }}</div>
						</div>
						@if (mailSettingsRow.isActive) {
							<span class="badge badge-success badge-sm" [translate]>Active</span>
						}
					</div>
					<div class="mt-3">
						<span class="badge badge-ghost badge-sm">{{ mailSettingsRow.kind }}</span>
					</div>
				</div>
			} @empty {
				<div class="alert alert-warning md:col-span-2 xl:col-span-3">
					<span [translate]>No email configuration found.</span>
				</div>
			}
		</div>

		<div class="bg-base-100 border-base-200 rounded-xl border p-4 shadow-sm">
			<label class="floating-label">
				<input
					class="input input-bordered w-full"
					type="text"
					[ngModel]="recipientFilter()"
					(ngModelChange)="setRecipientFilter($event)"
					placeholder="Filter by recipient"
				/>
				<span [translate]>Filter by recipient</span>
			</label>
		</div>

		<div class="bg-base-100 border-base-200 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm">
			<div class="min-h-0 flex-1 overflow-auto">
				<table class="table w-full">
					<thead class="bg-base-200/50 text-base-content/60 text-xs font-bold tracking-wider uppercase">
						<tr>
							<th>Email</th>
							<th>Sender</th>
							<th>Status</th>
							<th>Attempts</th>
							<th>Created</th>
							<th>Updated</th>
							<th class="text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						@for (mail of filteredMails(); track mail.id) {
							<tr
								class="hover:bg-base-200/30 align-top cursor-pointer"
								[class.bg-base-200/40]="isMailExpanded(mail.id)"
								[tabIndex]="0"
								[attr.aria-label]="'Expand details for email: ' + mail.subject"
								(click)="toggleExpandedMail(mail.id)"
								(keyup.enter)="toggleExpandedMail(mail.id)"
							>
								<td class="min-w-80">
									<div class="flex flex-col gap-1">
										<div class="font-medium">{{ mail.subject }}</div>
										<div class="text-sm opacity-80">{{ mail.recipients.join(', ') }}</div>
										<div class="font-mono text-xs opacity-60">{{ mail.id }}</div>
									</div>
								</td>
								<td class="text-sm">
									<div>{{ mail.sender }}</div>
									<div class="text-xs opacity-60">P{{ mail.priority }}</div>
								</td>
								<td>
									<span class="badge badge-sm" [class]="getMailStatusTone(mail.status)">{{ mail.status || 'unknown' }}</span>
								</td>
								<td>
									<span class="badge badge-ghost badge-sm">{{ mail.attemptsCount }}</span>
								</td>
								<td class="text-sm">{{ mail.timeCreated ? (mail.timeCreated | date: 'short') : '-' }}</td>
								<td class="text-sm">{{ mail.timeUpdated ? (mail.timeUpdated | date: 'short') : '-' }}</td>
								<td class="text-right">
									<button class="btn btn-ghost btn-xs" [disabled]="!!activeResendMailId()" (click)="handleResendClick(mail, $event)">
										@if (activeResendMailId() === mail.id) {
											<span class="loading loading-spinner loading-xs"></span>
										} @else {
											<span [translate]>Resend</span>
										}
									</button>
								</td>
							</tr>

							@if (isMailExpanded(mail.id)) {
								<tr>
									<td colspan="7" class="p-0">
										<div class="bg-base-200/40 border-base-200 grid gap-4 border-t p-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
											<div class="space-y-4">
												<div>
													<div class="mb-2 text-sm font-medium" [translate]>Plain text body</div>
													<pre class="bg-base-100 border-base-200 max-h-72 overflow-auto rounded-xl border p-3 text-xs whitespace-pre-wrap">{{ mail.body || 'No body available.' }}</pre>
												</div>

												@if (hasBodyHtml(mail)) {
													<div>
														<div class="mb-2 text-sm font-medium" [translate]>HTML body</div>
														<pre class="bg-base-100 border-base-200 max-h-72 overflow-auto rounded-xl border p-3 text-xs whitespace-pre-wrap">{{ mail.bodyHtml }}</pre>
													</div>
												}
											</div>

											<div>
												<div class="mb-2 text-sm font-medium" [translate]>Mail attempts</div>
												<div class="space-y-3">
													@for (mailAttempt of mail.attempts; track mailAttempt.id) {
														<div class="bg-base-100 border-base-200 rounded-xl border p-3">
															<div class="flex flex-wrap items-center justify-between gap-2">
																<span class="badge badge-sm" [class]="getAttemptStatusTone(mailAttempt.status)">{{ mailAttempt.status || 'unknown' }}</span>
																<span class="text-xs opacity-60">{{ mailAttempt.timeCreated ? (mailAttempt.timeCreated | date: 'short') : '-' }}</span>
															</div>
															<div class="mt-2 text-sm">
																<div>{{ mailAttempt.mailSettingsName || mailAttempt.mailSettingsId }}</div>
																@if (mailAttempt.mailSettingsKind) {
																	<div class="text-xs opacity-60">{{ mailAttempt.mailSettingsKind }}</div>
																}
															</div>
															@if (mailAttempt.error) {
																<pre class="bg-base-200 mt-3 max-h-40 overflow-auto rounded-lg p-2 text-xs whitespace-pre-wrap text-error">{{ mailAttempt.error }}</pre>
															}
														</div>
													} @empty {
														<div class="text-sm opacity-60" [translate]>No attempts recorded.</div>
													}
												</div>
											</div>
										</div>
									</td>
								</tr>
							}
						} @empty {
							<tr>
								<td colspan="7" class="py-10 text-center text-sm opacity-60" [translate]>No emails found.</td>
							</tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	}
</div>
